// TOP の「最近使ったキャンバス」カード用に、生成したサムネイルを localStorage へ持たせる。
//
// 表示側（recent-canvas-card.tsx）は `loadCanvasThumbnail(canvasId) ?? thumbnailUrl` と、
// ローカルキャッシュを S3 の URL より優先する。したがって「保存に失敗したまま古い値が残る」と、
// S3 に新しいサムネイルがあっても古いものが表示され続ける。保存が容量超過で失敗したときに
// 何もしないのではなく、他キャンバスのぶんを空けて撮り直す（下の 3 段方式）。

const KEY_PREFIX = "refinement-canvas:thumbnail:";

/** localStorage へ入れる値。savedAt は容量超過時の「古い順」削除に使う。 */
interface StoredThumbnail {
  readonly dataUrl: string;
  /** 保存時刻（epoch ms）。 */
  readonly savedAt: number;
}

export function loadCanvasThumbnail(canvasId: string): string | undefined {
  try {
    const raw = window.localStorage.getItem(`${KEY_PREFIX}${canvasId}`);
    if (raw === null) return undefined;
    const dataUrl = parseStoredDataUrl(raw);
    // data URL として使えない保存値（空文字・壊れた値）は、キャッシュ無しとして
    // S3 の URL へフォールバックさせる。
    return dataUrl.length > 0 ? dataUrl : undefined;
  } catch {
    return undefined;
  }
}

export function saveCanvasThumbnail(
  canvasId: string,
  dataUrl: string,
  options?: {
    /**
     * 保存しきれなかったときに、既存の値を残すか。
     * S3 の代替（meta.thumbnailUrl）がまだ無い場合に真を渡す。消してしまうと TOP のカードが
     * 空になり、現状より悪くなるためである（mock 認証、初回アップロード前、アップロード失敗中）。
     */
    readonly keepOnFailure?: boolean | undefined;
  }
): void {
  const key = `${KEY_PREFIX}${canvasId}`;
  const payload = JSON.stringify({ dataUrl, savedAt: Date.now() } satisfies StoredThumbnail);
  try {
    window.localStorage.setItem(key, payload);
    return;
  } catch {
    // 1 段目の失敗（多くは容量超過）。2 段目へ進む。
  }
  // 削除したエントリを控える。撮り直しても失敗するなら、消しただけ損になるため書き戻す。
  const evicted: { key: string; raw: string }[] = [];
  try {
    // 自分のキーを消しても容量は空かない（他キャンバスの高解像度キャッシュが占有している）ため、
    // 他キャンバスのぶんを古い順に空けてから 1 回だけ撮り直す。
    // 必要量は保存文字列の 2 倍の長さにする。quota の会計にはキー名や実装のオーバーヘッドも
    // 含まれるため、同じ長さだけ空けても収まらないことがあるためである（解放量と同じ
    // String#length ＝ UTF-16 code unit で数え、単位はそろえている）。
    evictOtherThumbnails(key, payload.length * 2, evicted);
    window.localStorage.setItem(key, payload);
    return;
  } catch {
    // 2 段目も失敗。空けた意味が無かったので他キャンバスのぶんを戻し、3 段目へ進む。
    restoreThumbnails(evicted);
  }
  // 3 段目。S3 の代替があるなら、古いキャッシュを残さず消してそちらへフォールバックさせる。
  if (options?.keepOnFailure === true) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // サムネイルは補助情報のため、削除もできない環境では何もせず編集を継続する。
  }
}

/**
 * 保存値から data URL を取り出す。
 * 新形式（{ dataUrl, savedAt } の JSON）と旧形式（素の data URL 文字列）の双方を受け付ける。
 * 旧形式のまま残っているキーは書き換えず、次の保存で自然に新形式へ置き換わる。
 *
 * どちらとしても解釈できない値（書き込みが途中で切れた JSON など）は空文字を返す。
 * そのまま返すと壊れた文字列が表示側の `url(...)` へ入り、S3 の URL へフォールバックできない。
 */
function parseStoredDataUrl(raw: string): string {
  // 旧形式は必ず data: で始まる。先に判定して、巨大な data URL の JSON.parse も避ける。
  if (raw.startsWith("data:")) return raw;
  const parsed = parseStored(raw);
  const dataUrl = parsed !== null && typeof parsed.dataUrl === "string" ? parsed.dataUrl : "";
  // 新形式でも、data URL として使えない値は表示側の `url(...)` へ渡さない。
  return dataUrl.startsWith("data:") ? dataUrl : "";
}

/** 保存値を新形式として解釈する。旧形式・壊れた値では null を返す。 */
function parseStored(raw: string): { readonly dataUrl?: unknown; readonly savedAt?: unknown } | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
    return parsed as { readonly dataUrl?: unknown; readonly savedAt?: unknown };
  } catch {
    return null;
  }
}

/**
 * 他キャンバスのサムネイルキーを「古い順」に削除し、targetChars ぶんの空きを作る。
 * 1 件消すごとに撮り直すと最悪 O(キー数) 回の setItem が走るため、まとめて空けてから 1 回だけ試す。
 * 空き容量の単位は localStorage の実装依存のため、保存文字列の長さを代理値として使う。
 *
 * 削除したエントリは evicted へ push する（途中で例外が起きても、そこまでの削除を呼び出し側が
 * 書き戻せるようにするため、戻り値ではなく引数の配列へ積む）。
 */
function evictOtherThumbnails(
  selfKey: string,
  targetChars: number,
  evicted: { key: string; raw: string }[]
): void {
  const entries: {
    readonly key: string;
    readonly raw: string;
    readonly savedAt: number;
  }[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key === null || key === selfKey || !key.startsWith(KEY_PREFIX)) continue;
    const raw = window.localStorage.getItem(key);
    if (raw === null) continue;
    entries.push({ key, raw, savedAt: resolveSavedAt(raw) });
  }
  // 旧形式は保存時刻が -Infinity になるため、差分ではなく大小比較で並べる
  // （-Infinity - -Infinity は NaN になり、比較関数として成立しない）。
  entries.sort((a, b) => (a.savedAt === b.savedAt ? 0 : a.savedAt < b.savedAt ? -1 : 1));
  let freed = 0;
  for (const entry of entries) {
    if (freed >= targetChars) break;
    window.localStorage.removeItem(entry.key);
    evicted.push({ key: entry.key, raw: entry.raw });
    freed += entry.raw.length;
  }
}

/** 撮り直しに失敗したときに、空けるために削除した他キャンバスのぶんを書き戻す（best-effort）。 */
function restoreThumbnails(evicted: readonly { readonly key: string; readonly raw: string }[]): void {
  for (const entry of evicted) {
    try {
      window.localStorage.setItem(entry.key, entry.raw);
    } catch {
      // 戻せない場合も、消したままにするのと同じ結果にしかならないため次へ進む。
    }
  }
}

/** 保存値の保存時刻。旧形式（savedAt を持たない）は最も古い扱いにして優先的に削除する。 */
function resolveSavedAt(raw: string): number {
  const parsed = parseStored(raw);
  return parsed !== null && typeof parsed.savedAt === "number" && Number.isFinite(parsed.savedAt)
    ? parsed.savedAt
    : Number.NEGATIVE_INFINITY;
}
